import { clearDiningSessionId, getDiningSessionBinding } from "./diningSessionStorage";
import { clearMySeatClaim } from "./diningSeatClaim";

export const TABLE_QR_SESSION_ENDED_EVENT = "dine-in-table-session-ended";

const key = (restaurantId) => `dineInTableQr:${String(restaurantId || "").trim()}`;
const payPrefKey = (restaurantId) => `dineInTableQrPayPref:${String(restaurantId || "").trim()}`;
const tabModeKey = (restaurantId) => `dineInTableQrTabMode:${String(restaurantId || "").trim()}`;

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readRawTableQrBlob(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return null;
  try {
    const fromSession = sessionStorage.getItem(key(rid));
    if (fromSession) return fromSession;
  } catch {
    /* ignore */
  }
  try {
    const fromLocal = localStorage.getItem(key(rid));
    if (fromLocal) {
      try {
        sessionStorage.setItem(key(rid), fromLocal);
      } catch {
        /* ignore */
      }
      return fromLocal;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Persists table QR context (token, label, capacity, optional party + seat picks).
 * Merges with existing storage when the same table token is still active.
 * @param {string} restaurantId
 * @param {{ token: string, label?: string, capacity?: number, partySize?: number, seatIds?: string[], qrOrderingEnabled?: boolean, qrPaymentEnabled?: boolean, resetSeatPicks?: boolean }} ctx
 */
export function setDineInTableQrContext(restaurantId, ctx) {
  const rid = String(restaurantId || "").trim();
  const token = String(ctx?.token || "").trim();
  if (!rid || !token || token.length < 16) return;

  let prev = null;
  try {
    prev = safeParse(readRawTableQrBlob(rid));
  } catch {
    prev = null;
  }

  const sameTable = prev && String(prev.token || "").trim() === token;
  if (!sameTable) {
    clearDiningSessionId(rid);
    try {
      sessionStorage.removeItem(tabModeKey(rid));
      localStorage.removeItem(tabModeKey(rid));
    } catch {
      /* ignore */
    }
  }
  const labelIn = ctx.label != null ? String(ctx.label).trim() : "";

  const next = {
    token,
    label: labelIn || (sameTable ? String(prev.label || "").trim() : "") || token,
  };

  const tableIdIn = ctx?.tableId != null ? String(ctx.tableId).trim() : "";
  if (tableIdIn) next.tableId = tableIdIn;
  else if (sameTable && String(prev?.tableId || "").trim()) next.tableId = String(prev.tableId).trim();

  if (typeof ctx?.qrOrderingEnabled === "boolean") {
    next.qrOrderingEnabled = ctx.qrOrderingEnabled;
  } else if (sameTable && typeof prev?.qrOrderingEnabled === "boolean") {
    next.qrOrderingEnabled = prev.qrOrderingEnabled;
  } else if (!sameTable) {
    next.qrOrderingEnabled = true;
  }

  if (typeof ctx?.qrPaymentEnabled === "boolean") {
    next.qrPaymentEnabled = ctx.qrPaymentEnabled;
  } else if (sameTable && typeof prev?.qrPaymentEnabled === "boolean") {
    next.qrPaymentEnabled = prev.qrPaymentEnabled;
  } else if (!sameTable) {
    next.qrPaymentEnabled = true;
  }

  const capIn = ctx?.capacity != null ? Number(ctx.capacity) : NaN;
  if (Number.isFinite(capIn) && capIn > 0) {
    next.capacity = Math.min(50, Math.floor(capIn));
  } else if (sameTable && Number(prev.capacity) > 0) {
    next.capacity = Math.min(50, Math.floor(Number(prev.capacity)));
  }

  if (ctx?.resetSeatPicks === true) {
    delete next.partySize;
    delete next.seatIds;
  } else if (!sameTable) {
    if (ctx?.partySize != null) next.partySize = Math.max(1, Math.min(50, Math.floor(Number(ctx.partySize))));
    if (Array.isArray(ctx?.seatIds) && ctx.seatIds.length > 0) {
      next.seatIds = ctx.seatIds.map((s) => String(s).trim());
    }
  } else {
    if (ctx?.partySize != null) next.partySize = Math.max(1, Math.min(50, Math.floor(Number(ctx.partySize))));
    else if (prev.partySize != null) next.partySize = prev.partySize;

    if (Array.isArray(ctx?.seatIds)) next.seatIds = ctx.seatIds.map((s) => String(s).trim());
    else if (prev.seatIds?.length) next.seatIds = prev.seatIds.map((s) => String(s).trim());
  }

  if (next.capacity && next.seatIds?.length) {
    const cap = next.capacity;
    const cleaned = [];
    for (const s of next.seatIds) {
      if (!/^\d+$/.test(s)) continue;
      const i = Number(s);
      if (i >= 0 && i < cap) cleaned.push(String(i));
    }
    const uniq = [...new Set(cleaned)];
    if (
      uniq.length !== next.seatIds.length ||
      !next.partySize ||
      uniq.length !== next.partySize
    ) {
      delete next.partySize;
      delete next.seatIds;
    } else {
      next.seatIds = uniq;
    }
  }

  const serialized = JSON.stringify(next);
  try {
    sessionStorage.setItem(key(rid), serialized);
    localStorage.setItem(key(rid), serialized);
  } catch (_) {
    /* ignore */
  }
}

export function getDineInTableQrContext(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return null;
  try {
    const raw = readRawTableQrBlob(rid);
    if (!raw) return null;
    const o = safeParse(raw);
    if (!o || typeof o !== "object") return null;
    const token = String(o.token || "").trim();
    if (!token || token.length < 16) return null;
    const label = String(o.label || "").trim() || token;
    const cap = Number(o.capacity);
    const out = { token, label };
    if (String(o.tableId || "").trim()) out.tableId = String(o.tableId).trim();
    if (Number.isFinite(cap) && cap > 0) out.capacity = Math.min(50, Math.floor(cap));
    if (o.partySize != null) {
      const p = Math.floor(Number(o.partySize));
      if (p >= 1) out.partySize = Math.min(50, p);
    }
    if (Array.isArray(o.seatIds) && o.seatIds.length > 0) {
      out.seatIds = o.seatIds.map((s) => String(s).trim());
    }
    if (typeof o.qrOrderingEnabled === "boolean") {
      out.qrOrderingEnabled = o.qrOrderingEnabled;
    }
    if (typeof o.qrPaymentEnabled === "boolean") {
      out.qrPaymentEnabled = o.qrPaymentEnabled;
    }
    return out;
  } catch {
    return null;
  }
}

export function readDineInTableQrToken(restaurantId) {
  return getDineInTableQrContext(restaurantId)?.token || null;
}

/** Options for setDiningSessionId — binds session to the current table QR. */
export function getTableQrDiningSessionOpts(restaurantId) {
  const ctx = getDineInTableQrContext(restaurantId);
  if (!ctx?.token) return {};
  const out = { tableQrToken: ctx.token };
  if (ctx.tableId) out.tableId = ctx.tableId;
  return out;
}

/** Table QR allows ordering (default true when a session exists and the flag is unset). */
export function isTableQrOrderingEnabledForRestaurant(restaurantId) {
  const c = getDineInTableQrContext(restaurantId);
  if (!c?.token || String(c.token).trim().length < 16) return false;
  return c.qrOrderingEnabled !== false;
}

/** Table QR allows guest payment (default true when a session exists and the flag is unset). */
export function isTableQrPaymentEnabledForRestaurant(restaurantId) {
  const c = getDineInTableQrContext(restaurantId);
  if (!c?.token || String(c.token).trim().length < 16) return false;
  return c.qrOrderingEnabled !== false && c.qrPaymentEnabled !== false;
}

/** Clears table QR + dining session binding on this device (staff closed tab, guest paid, etc.). */
export function endTableQrGuestExperience(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return false;
  const binding = getDiningSessionBinding(rid);
  if (binding?.sessionId) clearMySeatClaim(binding.sessionId);
  clearDiningSessionId(rid);
  clearDineInTableQrContext(rid);
  try {
    window.dispatchEvent(
      new CustomEvent(TABLE_QR_SESSION_ENDED_EVENT, { detail: { restaurantId: rid } }),
    );
  } catch {
    /* ignore */
  }
  return true;
}

/** Clears saved table + seats + pay preference for this restaurant (e.g. invalid QR). */
export function clearDineInTableQrContext(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return;
  try {
    sessionStorage.removeItem(key(rid));
    localStorage.removeItem(key(rid));
    sessionStorage.removeItem(payPrefKey(rid));
    localStorage.removeItem(payPrefKey(rid));
    sessionStorage.removeItem(tabModeKey(rid));
    localStorage.removeItem(tabModeKey(rid));
  } catch {
    /* ignore */
  }
}
export function getTableQrTabMode(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return "";
  try {
    let v = sessionStorage.getItem(tabModeKey(rid));
    if (v !== "PAY_LATER") {
      v = localStorage.getItem(tabModeKey(rid));
      if (v === "PAY_LATER") {
        try {
          sessionStorage.setItem(tabModeKey(rid), v);
        } catch {
          /* ignore */
        }
      }
    }
    return v === "PAY_LATER" ? "PAY_LATER" : "";
  } catch {
    return "";
  }
}

export function setTableQrTabMode(restaurantId, mode) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return;
  const v = mode === "PAY_LATER" ? "PAY_LATER" : "";
  try {
    if (!v) {
      sessionStorage.removeItem(tabModeKey(rid));
      localStorage.removeItem(tabModeKey(rid));
      return;
    }
    sessionStorage.setItem(tabModeKey(rid), v);
    localStorage.setItem(tabModeKey(rid), v);
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} restaurantId
 * @param {{ guestBillSplit?: "together"|"equal"|"items", splitInviteIndex?: number, splitMemberCount?: number }} [opts] — split hint + per-guest invite (0-based index, total N)
 */
export function buildRestaurantMenuUrlWithTableQr(restaurantId, opts = {}) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return "/explore";
  const tok = readDineInTableQrToken(rid);
  const split = opts.guestBillSplit;
  const splitQs =
    split && ["together", "equal", "items"].includes(String(split))
      ? `&guestBillSplit=${encodeURIComponent(String(split))}`
      : "";
  const n = Number(opts.splitMemberCount);
  const inv = Number(opts.splitInviteIndex);
  let inviteQs = "";
  if (
    Number.isFinite(n) &&
    n >= 2 &&
    n <= 50 &&
    Number.isFinite(inv) &&
    inv >= 0 &&
    inv < n
  ) {
    inviteQs = `&splitN=${encodeURIComponent(String(Math.floor(n)))}&splitInv=${encodeURIComponent(String(Math.floor(inv)))}`;
  }
  if (tok && tok.length >= 16) return `/restaurant/${rid}?tableQr=${encodeURIComponent(tok)}${splitQs}${inviteQs}`;
  return `/restaurant/${rid}`;
}

/**
 * @param {{ splitN?: number, splitInv?: number }} [opts] — carry equal-split invite slot to the Table tab
 */
export function buildDiningTableSessionUrl(restaurantId, opts = {}) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return "/explore";
  const base = `/restaurant/${rid}/dining/table`;
  const tok = readDineInTableQrToken(rid);
  const n = Number(opts.splitN);
  const inv = Number(opts.splitInv);
  let splitQs = "";
  if (Number.isFinite(n) && n >= 2 && n <= 50 && Number.isFinite(inv) && inv >= 0 && inv < n) {
    splitQs = `&splitN=${encodeURIComponent(String(Math.floor(n)))}&splitInv=${encodeURIComponent(String(Math.floor(inv)))}`;
  }
  if (tok && tok.length >= 16) return `${base}?tableQr=${encodeURIComponent(tok)}${splitQs}`;
  if (splitQs) return `${base}?${splitQs.slice(1)}`;
  return base;
}

/** Table QR: pay-later tab only (online pay hidden for now). */
export function getTableQrOrderPaymentPref(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (rid) {
    try {
      sessionStorage.setItem(payPrefKey(rid), "PAY_LATER");
      localStorage.setItem(payPrefKey(rid), "PAY_LATER");
    } catch {
      /* ignore */
    }
  }
  return "PAY_LATER";
}

export function setTableQrOrderPaymentPref(restaurantId, value) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return;
  const v = value === "PAY_NOW" ? "PAY_NOW" : "PAY_LATER";
  try {
    sessionStorage.setItem(payPrefKey(rid), v);
    localStorage.setItem(payPrefKey(rid), v);
  } catch (_) {
    /* ignore */
  }
}

export function isDineInTableQrSeatSetupComplete(restaurantId) {
  const c = getDineInTableQrContext(restaurantId);
  if (!c?.token || !Number(c.capacity) || c.capacity < 1) return false;
  const n = Number(c.partySize);
  const seats = c.seatIds;
  if (!Number.isFinite(n) || n < 1 || n > c.capacity) return false;
  if (!Array.isArray(seats) || seats.length !== n) return false;
  const uniq = new Set(seats);
  if (uniq.size !== n) return false;
  for (const s of seats) {
    if (!/^\d+$/.test(String(s))) return false;
    const i = Number(s);
    if (!Number.isFinite(i) || i < 0 || i >= c.capacity) return false;
  }
  return true;
}
